import React, { useState } from "react";
import { toast } from "sonner";
import {
  Share2, Link2, MessageCircle, Send, Twitter, Facebook, Linkedin, Mail, Copy, Check,
} from "@/lib/icons";

export const memberShareTitle = (name) => `${name || "PRONET Member"} | Photographer on PRONET`;

/**
 * Aesthetic share bar for public member profiles.
 * - Copy link
 * - Big social icons (WhatsApp, Telegram, X/Twitter, Facebook, LinkedIn, Email)
 * - Uses Web Share API when available (mobile)
 *
 * `onShared`, if given, fires once the user appears to have actually
 * completed a share — not just clicked a button. A click alone can't tell us
 * that (a wa.me link opening in a new tab proves nothing on its own), so we
 * arm a Page Visibility listener: if the tab goes hidden (they switched to
 * WhatsApp / Mail / etc. to finish sending) for at least ~1.5s and then
 * comes back, that's treated as a completed share. The native Web Share
 * API's resolved promise is a direct, immediate signal and skips the wait.
 */
export default function ShareBar({ url, title, subtitle, testidPrefix = "share", onShared }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url;
  const shareText = title || memberShareTitle();

  const armVisibilityUnlock = () => {
    if (!onShared) return;
    let hiddenAt = null;
    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt >= 1500) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        onShared();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    setTimeout(() => document.removeEventListener("visibilitychange", onVisibilityChange), 60000);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
      armVisibilityUnlock();
    } catch { toast.error("Copy failed"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareText, text: subtitle, url: shareUrl }); onShared?.(); } catch {}
    } else {
      copy();
    }
  };

  const enc = encodeURIComponent;
  const networks = [
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(shareText + "\n" + shareUrl)}`, tone: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
    { name: "Telegram", icon: Send, href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`, tone: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
    { name: "Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareText)}`, tone: "text-white/80 bg-white/5 border-white/15" },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, tone: "text-blue-300 bg-blue-500/10 border-blue-500/30" },
    { name: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`, tone: "text-indigo-300 bg-indigo-500/10 border-indigo-500/30" },
    { name: "Email", icon: Mail, href: `mailto:?subject=${enc(shareText)}&body=${enc(shareUrl)}`, tone: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/30" },
  ];

  return (
    <div className="glass rounded-3xl p-6" data-testid={`${testidPrefix}-bar`}>
      <div className="flex items-center gap-2 mb-4">
        <Share2 size={14} className="text-fuchsia-300" />
        <div className="overline text-white/60">Share this profile</div>
      </div>
      <button data-testid={`${testidPrefix}-native`} onClick={nativeShare}
        className="btn-glow w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white mb-3">
        <Share2 size={14} /> Share profile
      </button>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 truncate text-xs text-white/50 font-mono px-3 py-2 rounded-md bg-white/5 border border-white/10" data-testid={`${testidPrefix}-url`}>
          {shareUrl}
        </div>
        <button onClick={copy} data-testid={`${testidPrefix}-copy`}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white border border-white/15 bg-white/5 hover:border-fuchsia-500/50 transition-colors">
          {copied ? <Check size={12} className="text-emerald-300" /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {networks.map((n) => (
          <a key={n.name} href={n.href} target="_blank" rel="noreferrer" onClick={armVisibilityUnlock}
            data-testid={`${testidPrefix}-${n.name.toLowerCase()}`}
            title={`Share on ${n.name}`}
            className={`inline-flex flex-col items-center justify-center gap-1 rounded-xl border py-3 text-[10px] font-semibold uppercase tracking-widest transition-transform hover:-translate-y-0.5 ${n.tone}`}>
            <n.icon size={16} />
            <span>{n.name}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
